import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Lightweight admin-only hook that reports whether outbound email/SMS are
 * configured. Used to gate features that depend on those channels (down
 * payments, installments, attach-invoice-to-confirmation-email, etc.).
 *
 * Returns:
 *   {
 *     loading,         // initial fetch in flight
 *     emailConfigured, // SMTP creds present
 *     smsConfigured,   // SMS provider creds present
 *     emailReason,     // human-readable reason if !emailConfigured
 *     smsReason,       // human-readable reason if !smsConfigured
 *     refresh()        // re-fetch after admin saves comm settings
 *   }
 *
 * Failure is non-fatal: if the endpoint errors (e.g. non-admin user) we
 * fall back to "configured = true" so we never accidentally block features
 * for someone who can't fix the problem anyway.
 */
export default function useCommPreflight() {
  const [state, setState] = useState({
    loading: true,
    emailConfigured: true,
    smsConfigured: true,
    emailReason: null,
    smsReason: null,
  });

  const refresh = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const res = await axios.get(`${API_URL}/communication-settings/preflight`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setState({
        loading: false,
        emailConfigured: !!res.data.emailConfigured,
        smsConfigured: !!res.data.smsConfigured,
        emailReason: res.data.emailReason || null,
        smsReason: res.data.smsReason || null,
      });
    } catch (e) {
      // Non-admin (403) or transient network: assume configured so we don't
      // block features unfairly. The actual feature still no-ops gracefully
      // server-side if the channel isn't really set up.
      setState({
        loading: false,
        emailConfigured: true,
        smsConfigured: true,
        emailReason: null,
        smsReason: null,
      });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { ...state, refresh };
}
